import { Button } from "@/shared/components/shadcn/Button";
import { DetailSummary } from "@/shared/components/ui/DetailSummary";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { usePaymentSummary } from "../hooks/paymentQueries";
import { formatPaymentMoney, paymentStatusLabels } from "../types/paymentTypes";

export function AppointmentPayments({ appointmentId }: { appointmentId: string }) {
  const query = usePaymentSummary(appointmentId);
  const { goToAppointmentPayment } = useAppNavigation();
  const summary = query.data;
  return <Section title="Payments" card>
    {query.isPending ? <RowSkeleton /> : query.error ? <ErrorState message={query.error.message} onRetry={() => void query.refetch()} /> :
      summary && <>
        <p className="text-xs text-muted-foreground">{summary.environment === "test" ? "Test mode - no real money." : "Live payments"}</p>
        <DetailSummary items={[
          { label: "Agreed total", value: summary.totalMinor === null ? "Not set" : formatPaymentMoney(summary.totalMinor, summary.currency) },
          { label: "Collected", value: formatPaymentMoney(summary.collectedMinor, summary.currency) },
          { label: "Uncollected balance", value: summary.balanceMinor === null ? "Unavailable" : formatPaymentMoney(summary.balanceMinor, summary.currency) },
        ]} />
        <p className="text-xs text-muted-foreground">Requested deposits are not payment receipts. Refunds do not automatically reopen the balance for collection.</p>
        {summary.unavailableReason && <p className="text-sm">{summary.unavailableReason}</p>}
        {summary.activePayment && <Button variant="outline" onClick={() => goToAppointmentPayment(appointmentId, summary.activePayment!.id)}>Resume payment</Button>}
        {summary.payments.length === 0 ? <p className="text-sm text-muted-foreground">No payments recorded.</p> :
          summary.payments.map(payment => <InfoRow key={payment.id}
            title={formatPaymentMoney(payment.amountMinor, payment.currency)}
            subtitle={`${paymentStatusLabels[payment.status]} - ${payment.createdAt.toLocaleString()}`}
            caption={payment.refundedAmountMinor > 0 ? `Refunded ${formatPaymentMoney(payment.refundedAmountMinor, payment.currency)}` :
              payment.disputeStatus ? "Disputed - review in Stripe" : payment.card ? `${payment.card.brand} ending in ${payment.card.last4}` : undefined}
            onClick={() => goToAppointmentPayment(appointmentId, payment.id)} />)}
      </>}
    <Button variant="quiet" disabled={query.isFetching} onClick={() => void query.refetch()}>Refresh payments</Button>
  </Section>;
}
